<template>
  <div id="app">
    <OfflineBanner />
    <!-- Phase 2 (2026-08-25) — mounted at the root, above the layout, so a read
         failure is surfaced regardless of which page's listener failed and
         regardless of whether that page reads the composable's `error` ref.
         Outside the routerReady gate on purpose: a terminal data failure during
         the first navigation must still be explained, not hidden behind a shell
         that never renders. -->
    <DataHealthBanner />
    <div class="head">

    </div>
    <component :is="currentLayout" v-if="routerReady" />
    <transition-group name="fade" tag="div" class="notification-container">
      <div v-for="(notification, index) in notifications" :key="notification.id">
        <NotificationPopup :message="notification.message" :status="notification.status" :index="index"
          :id="notification.id" :count="notification.count" :persist="notification.persist"
          @closed="removeNotification" />
      </div>
    </transition-group>

    <!-- Popup for screen size warning -->
    <div v-if="showPopup" class="popup-container">
      <div class="popup-content">
        <p>Your screen is too small to view this page. Please use a desktop or a larger device.</p>
        <div class="button-container">
          <button @click="closePopup">Close</button>
        </div>
      </div>
    </div>
  </div>
  <!-- S0 (brief-security-pass, 2026-10-30) — the badge is ABSENT from the DOM
       everywhere but the three unauthenticated entry screens, not hidden. It
       was unconditional here, fixed bottom-left on every screen, and on a
       phone it sat over the chat composer's paperclip. Keyed by route NAME,
       not by `meta.layout === 'auth'`: the kiosk routes (clock-in, my-shifts)
       share that layout and are a staff member's working screen, not a
       landing page. See BETA_BADGE_ROUTE_NAMES. -->
  <div v-if="showBetaBadge" class="beta-label">Beta</div>
  <UpdatePrompt />
</template>

<script>
import { onMounted, ref } from 'vue';
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import AuthLayout from "@/layouts/AuthLayout.vue";
import SetupLayout from "@/layouts/SetupLayout.vue";
import notificationStore from "@/stores/notificationStore";
import NotificationPopup from "@/components/NotificationPopup.vue";
import UpdatePrompt from "@/components/UpdatePrompt.vue";
import OfflineBanner from "@/components/OfflineBanner.vue";
import DataHealthBanner from "@/components/DataHealthBanner.vue";
import { useScreenGuard } from '@/views/screenGuard';
import { useIdleLogout } from '@/composables/useIdleLogout';
import { useSessionRevocationWatch } from '@/composables/useSessionRevocationWatch';
import router from '@/router';

// S0 (brief-security-pass, 2026-10-30) — the ONLY routes that render the Beta
// badge, by route name. Login, signup and verification share AuthLayout and are
// the screens a person sees before they are anyone; everywhere else the badge
// is not rendered at all (v-if, not v-show). Stated negatively: this is NOT
// "every route whose layout is auth" — post-login, no-organisation, clock-in
// and my-shifts use that layout too and are deliberately excluded.
const BETA_BADGE_ROUTE_NAMES = ['login', 'signup', 'verification'];

export default {
  components: {
    DefaultLayout,
    AuthLayout,
    SetupLayout,
    NotificationPopup,
    UpdatePrompt,
    OfflineBanner,
    DataHealthBanner,
  },
  setup() {
    const { showPopup, closePopup } = useScreenGuard();
    useIdleLogout();
    // Phase 3 (2026-08-25) — mounted beside useIdleLogout, at the app root, so a
    // retired person is signed out mid-session instead of being left looking at
    // an app that has silently stopped working. See that composable's header for
    // why it is a sibling rather than part of useIdleLogout.
    useSessionRevocationWatch();
    const headHeight = ref(0);

    // routerReady stays false until the first navigation has fully resolved
    // (i.e. all beforeEnter guards have finished and Vue Router has decided
    // the final destination). This prevents <router-view> from rendering
    // anything at all before guards have had a chance to redirect, which is
    // the secondary defence against the flash on hard page loads / URL entry.
    //
    // Membership-revocation fix (2026-08-24) — router.isReady() REJECTS when
    // the first navigation fails: vue-router's markAsReady(err) rejects every
    // pending ready handler (and leaves `ready` false). Without the .catch
    // below, routerReady stayed false FOREVER and the `v-if` above rendered
    // nothing at all — a permanent white screen for ANY first-navigation
    // failure whatever its cause, plus an unhandled promise rejection whose
    // only stack frame is vue-router's own createRouterError. Rendering the
    // shell anyway is strictly better than rendering nothing: the user gets
    // the layout and whatever route the router did settle on, so a guard bug
    // degrades to a visible page instead of a blank one. This is the app's
    // only top-level guard against that failure class.
    const routerReady = ref(false);
    router.isReady()
      .then(() => {
        routerReady.value = true;
      })
      .catch((err) => {
        console.error('Initial navigation failed; rendering the app shell anyway:', err);
        routerReady.value = true;
      });

    onMounted(() => {
      const headElement = document.querySelector(".head");
      if (headElement) {
        headHeight.value = headElement.clientHeight;
        document.documentElement.style.setProperty(
          "--head-height",
          `${headHeight.value}px`
        );
      }
    });

    return {
      headHeight,
      showPopup,
      closePopup,
      routerReady,
    };
  },
  computed: {
    currentLayout() {
      const layout = this.$route.meta?.layout;
      if (layout === 'auth') return AuthLayout;
      if (layout === 'setup') return SetupLayout;
      return DefaultLayout;
    },
    notifications() {
      return notificationStore.state.notifications;
    },
    // Before the first navigation resolves `$route.name` is undefined, so the
    // badge is absent until the router has decided the screen is a login one.
    showBetaBadge() {
      return BETA_BADGE_ROUTE_NAMES.includes(this.$route?.name);
    },
  },
  methods: {
    removeNotification(id) {
      notificationStore.removeNotification(id);
    }
  }
};
</script>

<style>
#app {
  display: flex;
  flex-direction: column;
  font-family: "Avenir", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

.content {
  flex: 1;
  height: 100vh;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
}

@media screen and (max-width: 1499px) {
.content {
  overflow: unset;
}}

/* Notification styles */
.notification-container {
  position: fixed;
  /* Phase-A1 (2026-08-04): edge-touching corner element, safe-area insets. */
  bottom: calc(20px + var(--safe-bottom));
  right: calc(20px + var(--safe-right));
  z-index: 1500;
  display: flex;
  flex-direction: column-reverse;
  gap: 10px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.popup-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.popup-content {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
}

button {
  color: #ffffff;
  background-color: var(--purple);
  border-color: var(--purple);
  border-width: 1px;
  border-style: solid;
  padding: 8px 16px 8px 16px;
  border-radius: 8px;
  font-size: 0.875rem;
  line-height: 16px;
  font-weight: 500;
  margin: 0 4px;
}

.button-container {
  display: flex;
  justify-content: space-evenly;
  width: 100%;
}

.beta-label {
  position: fixed;
  /* Phase-A1 (2026-08-04): edge-touching corner element, safe-area insets. */
  bottom: calc(20px + var(--safe-bottom));
  /* Phase 2 (2026-08-26) — moved from the bottom-RIGHT corner, where it sat at
     the identical 20px offsets as .notification-container and the two simply
     overlapped: a toast covered the badge, and the badge covered the toast's
     lower edge. Three bottom-anchored elements now occupy three distinct
     positions — badge left, toasts right, update pill centre — so none can
     obscure another regardless of how many toasts are stacked. */
  left: calc(20px + var(--safe-left));
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: bold;
  /* Was 1000 — the SAME z-index as the report modal overlay
     (src/views/Reports.vue:647). A tie is broken by DOM order, and this badge
     is the last element in App.vue's template, so it painted OVER the modal
     and sat on top of the Shift Cost report's footer totals. Dropped below the
     modal: it is a decorative, pointer-events:none badge and must never cover
     content. */
  z-index: 900;
  pointer-events: none;
}

/* CRITICAL: Body margin to push content when sidebar is present */
body {
  margin: 0;
  padding: 0;
  transition: margin-left 0.3s ease;
}

/* Desktop: When sidebar is expanded (default) */
body.sidebar-expanded {
  margin-left: 230px;
  padding-top: 0;
}

/* Desktop: When sidebar is collapsed. 64px is `Navbar.vue :: .sidebar.collapsed`'s
   width; this read 70px, which left a 6px strip of bare page between the rail
   and every screen, beside the avatar at the bottom (measured 2026-10-29). */
body.sidebar-collapsed {
  margin-left: 64px;
  padding-top: 0;
}

/* Mobile: Remove sidebar margin, add top padding to clear the fixed
   .mobile-top-bar (56px tall, see Navbar.vue) plus breathing room so page
   content isn't flush against it. */
@media screen and (max-width: 768px) {
  body.sidebar-expanded,
  body.sidebar-collapsed {
    margin-left: 0 !important;
    padding-top: 74px;
  }
}

/* Not load-bearing, added then removed same-day (2026-08-24) — a
   min-width: 0 was added here speculatively while chasing a Dashboard
   mobile overflow bug up the ancestor tree. Checked and confirmed dead:
   .content is a flex item of #app (flex-direction: column), and
   flexbox's automatic-minimum-size floor only ever applies to a flex
   container's MAIN axis (height, for a column container) — never the
   cross axis (width) this override would have mattered for. The actual
   floor was a CSS Grid item further down (DashboardView.vue's .db-hero —
   grid's equivalent mechanism isn't restricted to a "main axis" the way
   flexbox's is), and the real fix was removing the forced-wide
   min-content at its source (that file's .db-hero-name, no longer
   white-space: nowrap) rather than fighting it with floor overrides up
   the chain. .content is global — every page's mobile layout goes
   through it — so this note is left here as a specific "don't re-add
   this speculatively" marker, not just deleted silently. */
</style>